var _excluded = ["children", "className"],
  _excluded2 = ["htmlProps"],
  _excluded3 = ["fontWeight", "fontSize", "isTruncated", "children"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import { forwardRef, omitThemingProps, StylesProvider, tqc, useMultiStyleConfig, useStyles } from '../../system';
import { cx, omit, __DEV__ } from '../../utils';
import * as React from 'react';
import { TabsProvider, useTab, useTabIndicator, useTabList, useTabPanel, useTabPanels, useTabs } from './useTabs';
import { Text } from '../layout';
/**
 * Tabs
 *
 * Provides context and logic for all tabs components. It doesn't render
 * any DOM node.
 */
export var Tabs = forwardRef((props, ref) => {
  var styles = useMultiStyleConfig('Tabs', props);
  var _omitThemingProps = omitThemingProps(props),
    {
      children,
      className
    } = _omitThemingProps,
    rest = _objectWithoutProperties(_omitThemingProps, _excluded);
  var _useTabs = useTabs(rest),
    {
      htmlProps
    } = _useTabs,
    ctx = _objectWithoutProperties(_useTabs, _excluded2);
  var context = React.useMemo(() => ctx, [ctx]);
  var rootProps = omit(htmlProps, ['isFitted']);
  return /*#__PURE__*/React.createElement(TabsProvider, {
    value: context
  }, /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, /*#__PURE__*/React.createElement(tqc.div, _extends({
    className: cx('tqc-tabs', className),
    ref: ref
  }, rootProps, {
    __css: styles.root
  }), children)));
});
if (__DEV__) {
  Tabs.displayName = 'Tabs';
}
/**
 * Tab button used to activate a specific tab panel. It renders a `button`,
 * and is responsible for automatic and manual selection modes.
 */
export var Tab = forwardRef((props, ref) => {
  var styles = useStyles();
  var tabProps = useTab(_objectSpread(_objectSpread({}, props), {}, {
    ref
  }));
  var tabStyles = _objectSpread({
    outline: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: {
      md: props.alignRight ? 'auto' : 0
    }
  }, styles.tab);
  return /*#__PURE__*/React.createElement(tqc.button, _extends({}, tabProps, {
    className: cx('tqc-tabs__tab', props.className),
    __css: tabStyles,
    tabIndex: 0
  }));
});
if (__DEV__) {
  Tab.displayName = 'Tab';
}
/**
 * TabList is used to manage a list of tab buttons. It renders a `div` by default,
 * and is responsible the keyboard interaction between tabs.
 */
export var TabList = forwardRef((props, ref) => {
  var tablistProps = useTabList(_objectSpread(_objectSpread({}, props), {}, {
    ref
  }));
  var styles = useStyles();
  var tablistStyles = _objectSpread({
    display: 'flex'
  }, styles.tablist);
  return /*#__PURE__*/React.createElement(tqc.div, _extends({}, tablistProps, {
    className: cx('tqc-tabs__tablist', props.className),
    __css: tablistStyles
  }));
});
if (__DEV__) {
  TabList.displayName = 'TabList';
}
/**
 * TabPanel
 * Used to render the content for a specific tab.
 */
export var TabPanel = forwardRef((props, ref) => {
  var panelProps = useTabPanel(_objectSpread(_objectSpread({}, props), {}, {
    ref
  }));
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.div, _extends({
    outline: "0"
  }, panelProps, {
    className: cx('tqc-tabs__tab-panel', props.className),
    __css: styles.tabpanel
  }));
});
if (__DEV__) {
  TabPanel.displayName = 'TabPanel';
}
/**
 * TabPanel
 *
 * Used to manage the rendering of multiple tab panels. It uses
 * `cloneElement` to hide/show tab panels.
 *
 * It renders a `div` by default.
 */
export var TabPanels = forwardRef((props, ref) => {
  var panelsProps = useTabPanels(props);
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.div, _extends({}, panelsProps, {
    width: "100%",
    ref: ref,
    className: cx('tqc-tabs__tab-panels', props.className),
    __css: styles.tabpanels
  }));
});
if (__DEV__) {
  TabPanels.displayName = 'TabPanels';
}
/**
 * TabIndicator
 *
 * Used to render an active tab indicator that animates between
 * selected tabs.
 */
export var TabIndicator = forwardRef((props, ref) => {
  var indicatorStyle = useTabIndicator();
  var style = _objectSpread(_objectSpread({}, props.style), indicatorStyle);
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.div, _extends({
    ref: ref
  }, props, {
    className: cx('tqc-tabs__tab-indicator', props.className),
    style: style,
    __css: styles.indicator
  }));
});
export var TabText = forwardRef((props, ref) => {
  var {
      fontWeight = 'lg',
      fontSize = 'inherit',
      isTruncated = true,
      children
    } = props,
    rest = _objectWithoutProperties(props, _excluded3);
  return /*#__PURE__*/React.createElement(Text, _extends({
    fontWeight: fontWeight,
    fontSize: fontSize,
    isTruncated: isTruncated,
    ref: ref
  }, rest), children);
});
if (__DEV__) {
  TabIndicator.displayName = 'TabIndicator';
}